import React, { useEffect, useState, useMemo } from "react";
import { supabase } from "../../supabaseClient";
import {
    Box,
    Typography,
    Grid,
    LinearProgress,
    Button,
    Card,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/DownloadOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import FlashOnOutlinedIcon from "@mui/icons-material/FlashOnOutlined";
import RadioButtonUncheckedOutlinedIcon from "@mui/icons-material/RadioButtonUncheckedOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import { errorTost, successTost } from "../../Services/Toast";

/* ------------------ Helpers ------------------ */

const formatIndianNumber = (num) => {
    if (num === undefined || num === null) return "0";
    const fixed = Number(num).toFixed(2);
    const [intPart, decPart] = fixed.split(".");
    const lastThree = intPart.slice(-3);
    const rest = intPart.slice(0, -3);
    const formatted =
        rest.length > 0
            ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree
            : lastThree;
    return `${formatted}.${decPart}`;
};

const formatCurrencyShort = (num) => {
    const n = Number(num || 0);
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} CR`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
    return `₹${formatIndianNumber(n)}`;
};

const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};


const downloadCSV = (rows, planName = "POP_Plan") => {
    if (!rows || rows.length === 0) return;

    const headers = ["Project", "Value", "Brokerage", "Date"];

    const csvRows = [
        headers.join(","),
        ...rows.map((row) => {
            const project = row?.Projects?.projectName || "-";
            const value = row.booking_value || 0;
            const brokerage = row.pop_amount || 0;
            const date = row.created_at
                ? new Date(row.created_at).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                })
                : "-";

            return [project, value, brokerage, date]
                .map((item) => `"${item}"`)
                .join(",");
        }),
    ].join("\n");

    const blob = new Blob([csvRows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${planName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const getSlabLabel = (slab) => {
    if (slab?.percentage != null) return `${Number(slab.percentage).toFixed(0)}%`;
    if (slab?.amount != null) return formatCurrencyShort(Number(slab.amount));
    return "-";
};

const CardBox = ({ children, sx = {} }) => (
    <Card
        sx={{
            p: 2.5,
            borderRadius: 3,
            boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
            border: "1px solid #EEF2F7",
            background: "#FFFFFF",
            ...sx,
        }}
    >
        {children}
    </Card>
);

const AnimatedProgressBar = ({ value }) => {
    const safeValue = Number(value) || 0;
    const targetValue = safeValue > 0 ? Math.max(Math.min(safeValue, 100), 2) : 0;
    const [displayValue, setDisplayValue] = useState(0);

    useEffect(() => {
        const timer = setTimeout(() => setDisplayValue(targetValue), 100);
        return () => clearTimeout(timer);
    }, [targetValue]);

    return (
        <Box sx={{ mt: 1.5 }}>
            <LinearProgress
                variant="determinate"
                value={displayValue}
                sx={{
                    height: 8,
                    borderRadius: 999,
                    backgroundColor: "rgba(255,255,255,0.18)",
                    "& .MuiLinearProgress-bar": {
                        borderRadius: 999,
                        background: "linear-gradient(90deg, #ffffff, #D1FAE5)",
                        transition: "transform 0.9s cubic-bezier(0.4,0,0.2,1)",
                    },
                }}
            />
        </Box>
    );
};

/* ------------------ Main Component ------------------ */

const POPDetailsPage = ({ onBack, selectedPlan, cpId }) => {




    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [paidProjects, setPaidProjects] = useState({});
    const [withdrawLoading, setWithdrawLoading] = useState(false);
    const [isPopWithdrawn, setIsPopWithdrawn] = useState(false);



    console.log("Selected POP Plan:", selectedPlan);


    const { plan, slabs, stats, rows = [] } = selectedPlan;
    const projectId = rows?.[0]?.Projects?.projectId || null;
    const enablePayout = rows?.[0]?.enablePayout ?? false;
    useEffect(() => {
        const alreadyDone = rows.some((r) => r.isCpPaymentEntryDone);
        setIsPopWithdrawn(alreadyDone);
    }, [rows]);

    if (!selectedPlan) return null;

    const popPlan = plan || null;
    const popSlabs = slabs || [];
    const popData = stats || {
        totalUnits: 0,
        totalReward: 0,
        target: 0,
        achievement: 0,
    };

    const currentSlab =
        popSlabs.find((slab) => {
            const from = Number(slab.from_value || 0);
            const to = slab.to_value ? Number(slab.to_value) : Infinity;
            return popData.totalUnits >= from && popData.totalUnits <= to;
        }) || popSlabs[0];

    const nextSlab = popSlabs.find(
        (slab) => popData.totalUnits < Number(slab.from_value || 0)
    );

    const nextTarget = getSlabLabel(nextSlab || currentSlab);
    const unitsNeeded = nextSlab
        ? Math.max(0, Number(nextSlab.from_value || 0) - Number(popData.totalUnits || 0))
        : 0;

    const currentLevel = Math.max(
        1,
        popSlabs.findIndex((slab) => {
            const to = slab.to_value ? Number(slab.to_value) : Infinity;
            return popData.totalUnits <= to;
        }) + 1 || popSlabs.length
    );
    const getShortProjectName = (name = "") => {
        if (!name) return "-";

        const words = name.trim().split(" ");

        // 1 word → return first 6 chars
        if (words.length === 1) return words[0].slice(0, 6);

        // 2 words → first 3 + first 3
        if (words.length === 2)
            return `${words[0].slice(0, 3)} ${words[1].slice(0, 3)}`;

        // 3+ words → initials (best UX)
        return words.map((w) => w[0]).join("").toUpperCase();
    };


    const triggerCpPayment = async (payload) => {
        try {
            const res = await fetch(
                `${process.env.REACT_APP_SEND_OTP_MESSAGE_A2P_SERVICE}api/cp-payment/trigger-cp-payment`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                }
            );

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.message || "Something went wrong");
            }

            return data;

        } catch (err) {
            console.error("❌ API Error:", err);
            throw err;
        }
    };



    return (
        <Box
            sx={{
                  height: { xs: "80vh", md: "90vh" },
                display: "flex",
                flexDirection: "column",
                background: "#F5F7FB",
                overflow: "hidden",
            }}
        >
            {/* HEADER */}
            <Box
                sx={{
                    px: { xs: 2, md: 3 },
                    py: 1.8,
                    // background: "#FFFFFF",
                    borderBottom: "1px solid #E5E7EB",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                }}
            >
                <Box
                    display="flex"
                    alignItems="center"
                    sx={{ cursor: "pointer", "&:hover": { opacity: 0.8 } }}
                    onClick={onBack}
                >
                    <ArrowBackIcon sx={{ mr: 1, fontSize: 20, color: "#374151" }} />
                    <Box>
                        <Typography
                            fontWeight={700}
                            fontSize={{ xs: 15, sm: 18 }}
                            color="#111827"
                            lineHeight={1.2}
                        >
                            {popPlan?.name || "POP Plan"}
                        </Typography>

                        <Box display="flex" alignItems="center" gap={0.8} mt={0.3}>
                            <CalendarTodayOutlinedIcon sx={{ fontSize: 12, color: "#9CA3AF" }} />
                            <Typography fontSize={{ xs: 11, sm: 12 }} color="#9CA3AF">
                                {formatDate(popPlan?.start_date)} –{" "}
                                {formatDate(popPlan?.end_date)}
                            </Typography>
                        </Box>

                        <Typography fontSize={{ xs: 10, sm: 11 }} color="#B45309" mt={0.4}>
                            Agreement done leads only
                        </Typography>
                    </Box>
                </Box>

                <Button
                    variant="outlined"
                    startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                    sx={{
                        textTransform: "none",
                        borderRadius: 2.5,
                        fontSize: { xs: 10, sm: 12 },
                        fontWeight: 600,
                        color: "#374151",
                        borderColor: "#D1D5DB",
                        px: 2,
                        py: 0.8,
                        "&:hover": {
                            borderColor: "#9CA3AF",
                            background: "#F9FAFB",
                        },
                    }}
                    onClick={() => downloadCSV(rows, popPlan?.name || "POP_Plan")}
                >
                    Download Excel
                </Button>

            </Box>

            {/* CONTENT */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: "auto",
                    overflowX: "hidden",
                    px: { xs: 2, md: 3 },
                    py: 2.5,
                  pb: { xs: 14, sm: 15 },
                }}
            >
                {/* HERO */}
                <Box
                    sx={{
                        p: { xs: 2, md: 2.5 },
                        borderRadius: 4,
                        background: "linear-gradient(90deg, #34D399 0%, #14B8A6 100%)",
                        color: "#fff",
                        mb: 2.5,
                        position: "relative",
                        overflow: "hidden",
                    }}
                >
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <Typography fontSize={11} fontWeight={700} sx={{ opacity: 0.9, mb: 0.6 }}>
                                OVERALL ACHIEVEMENT
                            </Typography>

                            <Box display="flex" alignItems="baseline" gap={1}>
                                <Typography fontSize={{ xs: 34, sm: 42 }} fontWeight={800} lineHeight={1}>
                                    {Number(popData.achievement || 0).toFixed(0)}%
                                </Typography>
                                <Typography fontSize={13} sx={{ opacity: 0.9 }}>
                                    of target
                                </Typography>
                            </Box>

                            <AnimatedProgressBar value={popData.achievement} />

                            <Box mt={1.2} display="flex" justifyContent="space-between">
                                <Typography fontSize={11} sx={{ opacity: 0.85 }}>
                                    0 NOS
                                </Typography>
                                <Typography fontSize={11} sx={{ opacity: 0.85 }}>
                                    Target: {popData.target || 0} NOS
                                </Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={4}>
                            <Box display="flex" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.2,
                                        background: "rgba(255,255,255,0.10)",
                                        border: "1px solid rgba(255,255,255,0.12)",
                                        px: 2,
                                        py: 1.2,
                                        borderRadius: 3,
                                    }}
                                >
                                    <Box>
                                        <Typography fontSize={10} sx={{ opacity: 0.8 }} textAlign="right">
                                            CURRENT SLAB
                                        </Typography>
                                        <Typography fontSize={22} fontWeight={800} textAlign="right">
                                            {getSlabLabel(currentSlab)}
                                        </Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            width: 34,
                                            height: 34,
                                            borderRadius: "50%",
                                            background: "rgba(255,255,255,0.14)",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        <EmojiEventsOutlinedIcon sx={{ fontSize: 18, color: "#fff" }} />
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>

                {/* STATS */}
                <Grid container spacing={2} mb={2.5}>
                    {[
                        { title: "UNITS SOLD", value: `${popData.totalUnits || 0} NOS`, icon: "#" },
                        {
                            title: "REWARD EARNED",
                            value: formatCurrencyShort(popData.totalReward || 0),
                            icon: "₹",
                            isReward: true,
                        },
                        { title: "TARGET UNITS", value: `${popData.target || 0} NOS`, icon: "◎" },
                    ].map((item, i) => (
                        <Grid item xs={12} md={4} key={i}>
                            <CardBox sx={{ minHeight: 110 }}>
                                <Box display="flex" alignItems="center" gap={1} mb={1}>
                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: "50%",
                                            background: "#EEF2FF",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: 14,
                                            color: item.isReward ? "#F59E0B" : "#6B7280",
                                        }}
                                    >
                                        {item.icon}
                                    </Box>
                                    <Typography fontSize={11} fontWeight={700} color="#9CA3AF">
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Typography
                                    fontWeight={800}
                                    fontSize={{ xs: 24, sm: 28 }}
                                    color={item.isReward ? "#10B981" : "#111827"}
                                >
                                    {item.value}
                                </Typography>

                                {item.isReward && (
                                    <Typography fontSize={12} color="#9CA3AF" mt={0.8}>
                                        FY end payout
                                    </Typography>
                                )}
                            </CardBox>
                        </Grid>
                    ))}
                </Grid>

                {/* WHAT'S NEXT */}
                <Box
                    sx={{
                        p: 2,
                        borderRadius: 3,
                        background: "#FEFCE8",
                        border: "1px solid #FDE68A",
                        mb: 2.5,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                    }}
                >
                    <Box display="flex" alignItems="center" gap={1.2}>
                        <Box
                            sx={{
                                width: 34,
                                height: 34,
                                borderRadius: "50%",
                                background: "#F59E0B",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <FlashOnOutlinedIcon sx={{ color: "#78350F", fontSize: 18 }} />
                        </Box>
                        <Box>
                            <Typography fontSize={13} fontWeight={700} color="#92400E">
                                What’s Next?
                            </Typography>
                            <Typography fontSize={12} color="#78716C">
                                Close {unitsNeeded} more deals to unlock {nextTarget} reward
                            </Typography>
                        </Box>
                    </Box>

                    <Box textAlign="right">
                        <Typography fontSize={10} color="#A16207" fontWeight={700}>
                            NEXT LEVEL
                        </Typography>
                        <Typography fontSize={22} fontWeight={800} color="#F59E0B">
                            {nextTarget}
                        </Typography>
                    </Box>
                </Box>

                {/* MILESTONE JOURNEY */}
                <CardBox sx={{ mb: 2.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Box>
                            <Typography fontWeight={700} fontSize={14} color="#111827">
                                Your Milestone Journey
                            </Typography>
                            <Typography fontSize={11} color="#9CA3AF">
                                Level {currentLevel} of {popSlabs.length}
                            </Typography>
                        </Box>
                        <Box textAlign="right">
                            <Typography fontSize={10} color="#9CA3AF" fontWeight={700}>
                                NEXT TARGET
                            </Typography>
                            <Typography fontSize={18} fontWeight={800} color="#4F46E5">
                                {nextTarget}
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            position: "relative",
                            flexWrap: "nowrap",
                            overflowX: "auto",
                            py: 1,
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                top: 30,
                                left: 0,
                                right: 0,
                                height: 2,
                                background: "#E5E7EB",
                                zIndex: 0,
                            }}
                        />

                        {popSlabs.map((slab, idx) => {
                            const from = Number(slab.from_value || 0);
                            const to = slab.to_value ? Number(slab.to_value) : Infinity;
                            const isCompleted = Number(popData.totalUnits || 0) > to;
                            const isCurrent =
                                Number(popData.totalUnits || 0) >= from &&
                                Number(popData.totalUnits || 0) <= to;

                            return (
                                <Box
                                    key={slab.id}
                                    sx={{
                                        position: "relative",
                                        zIndex: 1,
                                        minWidth: 120,
                                        textAlign: "center",
                                        px: 1,
                                        animation: "popSlabFadeIn 0.5s ease both",
                                        animationDelay: `${idx * 0.1}s`,
                                        "@keyframes popSlabFadeIn": {
                                            from: { opacity: 0, transform: "scale(0.7) translateY(10px)" },
                                            to: { opacity: 1, transform: "scale(1) translateY(0)" },
                                        },
                                    }}
                                >
                                    <Typography
                                        fontSize={12}
                                        fontWeight={700}
                                        color={isCurrent ? "#4F46E5" : isCompleted ? "#16A34A" : "#9CA3AF"}
                                        mb={1}
                                    >
                                        {getSlabLabel(slab)}
                                    </Typography>

                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: "50%",
                                            mx: "auto",
                                            background: isCompleted
                                                ? "#4F46E5"
                                                : isCurrent
                                                    ? "#FBBF24"
                                                    : "#F3F4F6",
                                            border: isCompleted || isCurrent ? "none" : "1px solid #D1D5DB",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color: isCompleted || isCurrent ? "#fff" : "#9CA3AF",
                                            boxShadow: isCurrent ? "0 0 0 6px rgba(251,191,36,0.18)" : "none",
                                            transition: "all 0.5s ease",
                                        }}
                                    >
                                        {isCompleted ? (
                                            "✓"
                                        ) : isCurrent ? (
                                            "→"
                                        ) : (
                                            <LockOutlinedIcon sx={{ fontSize: 14 }} />
                                        )}
                                    </Box>

                                    <Typography fontSize={10} color="#6B7280" mt={1} fontWeight={700}>
                                        {from}-{to === Infinity ? "∞" : to} NOS
                                    </Typography>
                                    <Typography fontSize={9} color="#9CA3AF">
                                        {isCompleted ? "Unlocked" : isCurrent ? "Next" : "Locked"}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    <Box
                        sx={{
                            mt: 2,
                            p: 1.5,
                            borderRadius: 2.5,
                            background: "#FFFBEB",
                            border: "1px solid #FDE68A",
                        }}
                    >
                        <Typography fontSize={12} color="#92400E" fontWeight={700}>
                            Reach {nextTarget} by closing {unitsNeeded} more deals
                        </Typography>
                        <Typography fontSize={11} color="#78716C">
                            Keep pushing — you’re getting closer to the next reward.
                        </Typography>
                    </Box>
                </CardBox>
                {enablePayout && (
                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        alignItems="center"
                        mb={2}
                    >
                        <Button
                            variant="contained"
                            disabled={isPopWithdrawn || popData.totalReward === 0}
                            onClick={() => {
                                if (isPopWithdrawn) return;
                                setConfirmOpen(true);
                            }}
                            sx={{
                                textTransform: "none",
                                fontSize: { xs: 11, sm: 12 },
                                fontWeight: 400,
                                borderRadius: 2,
                                px: 3,
                                py: 1,
                            }}
                        >
                            {isPopWithdrawn ? "Already Withdrawn" : "Withdraw POP"}
                        </Button>
                    </Box>
                )}

                {/* TABLE */}
                <CardBox>
                    <Typography mb={2} fontSize={14} fontWeight={700} color="#111827">
                        Booking-wise Breakdown
                    </Typography>

                    <Box sx={{ overflowX: "auto" }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    {["PROJECT", "VALUE", "BROKERAGE", "AGREEMENT DATE"].map((h) => (
                                        <TableCell
                                            key={h}
                                            sx={{
                                                fontSize: 12,
                                                fontWeight: 700,
                                                color: "#6B7280",
                                                letterSpacing: 0.4,
                                                borderBottom: "1px solid #F3F4F6",
                                                pb: 1.2,
                                                whiteSpace: "nowrap",
                                            }}
                                        >
                                            {h}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} align="center">
                                            <Typography fontSize={13} color="#9CA3AF">
                                                No bookings found
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((row, i) => (
                                        <TableRow
                                            key={i}
                                            sx={{
                                                "&:last-child td": { border: 0 },
                                                "&:hover": { background: "#F9FAFB" },
                                                "& td": { borderBottom: "1px solid #F9FAFB" },
                                                transition: "0.2s ease",
                                            }}
                                        >

                                            <TableCell
                                                sx={{ fontSize: 13, color: "#374151" }}
                                                title={row?.Projects?.projectName}
                                            >
                                                {row?.Projects?.projectShortCode ||
                                                    getShortProjectName(row?.Projects?.projectName) ||
                                                    "-"}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13, color: "#111827", fontWeight: 700 }}>
                                                {formatCurrencyShort(row.booking_value || 0)}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13, color: "#10B981", fontWeight: 800 }}>
                                                {formatCurrencyShort(row.pop_amount || 0)}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: 13, color: "#6B7280" }}>
                                                {formatDate(row.agreement_date)}
                                            </TableCell>


                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                </CardBox>
            </Box>
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirm Withdrawal</DialogTitle>

                <DialogContent>
                    <Typography fontSize={14}>
                        Are you sure you want to withdraw POP payment for{" "}
                        <strong>{rows?.[0]?.Projects?.projectName || "this POP plan"}</strong>?
                    </Typography>
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={withdrawLoading}>
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        disabled={withdrawLoading}
                        onClick={async () => {
                            try {
                                setWithdrawLoading(true);

                                const payload = {
                                    cpId: cpId,
                                    aopId: null,
                                    popId: selectedPlan?.plan?.id,
                                    projectId: projectId,
                                };

                                await triggerCpPayment(payload);

                                // 🔥 UPDATE UI
                             setIsPopWithdrawn(true);

                                successTost("POP Withdrawal Successful ✅");
                                setConfirmOpen(false);
                            } catch (err) {
                                console.error(err);
                                errorTost("Failed to withdraw POP");
                            } finally {
                                setWithdrawLoading(false);
                            }
                        }}
                    >
                        {withdrawLoading ? "Processing..." : "Confirm"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default POPDetailsPage;